// Locale-aware formatting. Prices are USD in every locale for now.
import { LOCALE_CONFIG, type Locale } from "./locales";

export function formatNumber(locale: Locale, value: number, options?: Intl.NumberFormatOptions) {
  return new Intl.NumberFormat(LOCALE_CONFIG[locale].intl, options).format(value);
}

/** formatPrice("en", 5) → "$5"; formatPrice("zh-Hant", 5) → "US$5"; formatPrice("es", 5) → "US$5". Whole amounts drop the cents. */
export function formatPrice(locale: Locale, amount: number) {
  const whole = Number.isInteger(amount);
  const text = new Intl.NumberFormat(LOCALE_CONFIG[locale].intl, {
    style: "currency", currency: LOCALE_CONFIG[locale].currency,
    minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2,
  }).format(amount);
  // zh-TW renders "US$5" already; en-US renders "$5".
  // Spanish: "$" is the local peso in Latin America and es-ES writes "5 US$", so spell out "US$5".
  if (locale === "es") return `US$${formatNumber(locale, amount, { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 })}`;
  return text;
}

export function formatDate(locale: Locale, value: Date | string | number, options: Intl.DateTimeFormatOptions = { year: "numeric", month: "short", day: "numeric" }) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(LOCALE_CONFIG[locale].intl, options).format(date);
}

export function formatDateTime(locale: Locale, value: Date | string | number, options: Intl.DateTimeFormatOptions = { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) {
  return formatDate(locale, value, options);
}

export function formatList(locale: Locale, items: string[], type: "conjunction" | "disjunction" = "conjunction") {
  return new Intl.ListFormat(LOCALE_CONFIG[locale].intl, { style: "long", type }).format(items);
}
